import {
	faExclamationTriangle,
	faEye,
	faPencilAlt,
	faSpinner,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import SidePanel from "@/components/Sidepanel";
import { FlowType, useFlowTypeStore } from "@/store/flowTypes.store";
import { formatDateWithTime } from "@/utils/dateFormatter";
import { handleApiError } from "@/utils/errorHandler";
import AddFlowTypeForm from "./AddFlowTypeForm";

const FLOW_TYPES_QUERY_KEY = ["flow-types"] as const;

const ViewFlowType = () => {
	const navigate = useNavigate();

	const [isDeleting, setIsDeleting] = useState(false);

	const {
		flowTypes,
		selectedFlowType,
		isPanelOpen,
		isConfirmModalOpen,
		flowTypeToDelete,
		setFlowTypes,
		setSelectedFlowType,
		setPanelOpen,
		setConfirmModalOpen,
		setFlowTypeToDelete
	} = useFlowTypeStore();

	const {
		isLoading,
		isError,
		error,
		data: queryFlowTypes,
		refetch
	} = useQuery({
		queryKey: FLOW_TYPES_QUERY_KEY,
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_TYPES", "LIST"])
			);
			setFlowTypes(data);
			return data;
		},
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		initialData: flowTypes.length > 0 ? flowTypes : undefined
	});

	// Use query data if available, otherwise use store data
	const displayFlowTypes = queryFlowTypes || flowTypes;

	const handleViewClick = (flowType: FlowType) => {
		navigate(`/admin/integrations/${flowType.id}`);
	};

	const handleEditClick = (flowType: FlowType) => {
		setSelectedFlowType(flowType);
		setPanelOpen(true);
	};

	const handleDeleteClick = (flowType: FlowType) => {
		setFlowTypeToDelete(flowType);
		setConfirmModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (flowTypeToDelete) {
			try {
				setIsDeleting(true);
				await apiClient.delete(
					resolveEndpoint(
						API_ENDPOINTS,
						["FLOW_TYPES", "DELETE_BY_ID"],
						flowTypeToDelete.id
					)
				);
				await refetch();
				setConfirmModalOpen(false);
				setFlowTypeToDelete(null);
				toast.success("Flow type deleted successfully");
			} catch (error: unknown) {
				if (error && typeof error === "object" && "response" in error) {
					const apiError = error as {
						response: { data: { error: { message: string } } };
					};
					toast.error(apiError.response.data.error.message);
				} else {
					toast.error("An unexpected error occurred");
				}
			} finally {
				setIsDeleting(false);
			}
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	if (isError) {
		handleApiError(error, "Failed to load flow types");
		return (
			<div className="flex items-center justify-center h-screen text-red-600">
				Error: Failed to load flow types
			</div>
		);
	}

	return (
		<div className="flex-1">
			<div className="flex justify-end items-center mb-6">
				<button
					type="button"
					onClick={() => {
						setSelectedFlowType(null);
						setPanelOpen(true);
					}}
					className="bg-blue-fynxt text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
				>
					Create Integration
				</button>
			</div>
			<div className="bg-white shadow-sm rounded-lg overflow-x-auto overflow-y-auto">
				<table className="min-w-full divide-y divide-gray-200 custom-table">
					<thead className="bg-gray-100 sticky top-0 z-10">
						<tr>
							<th
								scope="col"
								className="px-6 text-center text-xs font-medium text-gray-900 uppercase tracking-wider"
							>
								Name
							</th>
							<th
								scope="col"
								className="px-6 text-center text-xs font-medium text-gray-900 uppercase tracking-wider"
							>
								Created At
							</th>
							<th
								scope="col"
								className="px-6 text-center text-xs font-medium text-gray-900 uppercase tracking-wider"
							>
								Updated At
							</th>
							<th
								scope="col"
								className="px-6 text-center text-xs font-medium text-gray-900 uppercase tracking-wider"
							>
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{displayFlowTypes.length > 0 ? (
							displayFlowTypes.map((flowType: FlowType) => (
								<tr key={flowType.id} className="hover:bg-gray-50">
									<td className="px-6 whitespace-nowrap text-center text-sm text-gray-500">
										{flowType.name}
									</td>
									<td className="px-6 whitespace-nowrap text-center text-sm text-gray-500">
										{formatDateWithTime(flowType.createdAt)}
									</td>
									<td className="px-6 whitespace-nowrap text-center text-sm text-gray-500">
										{formatDateWithTime(flowType.updatedAt)}
									</td>
									<td className="px-6 whitespace-nowrap text-center text-sm text-gray-500">
										<div className="flex justify-center space-x-2">
											<button
												type="button"
												onClick={() => handleViewClick(flowType)}
												className="text-blue-600 hover:text-blue-900 p-1"
												title="View Details"
											>
												<FontAwesomeIcon icon={faEye} />
											</button>
											<button
												type="button"
												onClick={() => handleEditClick(flowType)}
												className="text-green-600 hover:text-green-900 p-1 ml-[5px]"
												title="Edit"
											>
												<FontAwesomeIcon icon={faPencilAlt} />
											</button>
											<button
												type="button"
												onClick={() => handleDeleteClick(flowType)}
												className="text-red-600 hover:text-red-900 p-1"
												title="Delete"
											>
												<FontAwesomeIcon icon={faTrash} />
											</button>
										</div>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={4}
									className="px-6 py-6 text-center text-gray-400 text-sm whitespace-nowrap"
								>
									No integrations found.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<SidePanel
				isOpen={isPanelOpen}
				onClose={() => setPanelOpen(false)}
				title={selectedFlowType ? "Edit Integration" : "Create Integration"}
				closeOnOutsideClick={false}
			>
				<AddFlowTypeForm
					flowType={selectedFlowType}
					onClose={() => setPanelOpen(false)}
				/>
			</SidePanel>

			{isConfirmModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity"
							aria-hidden="true"
						>
							<div className="absolute inset-0 bg-gray-500 opacity-75" />
						</div>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
										<FontAwesomeIcon
											icon={faExclamationTriangle}
											className="h-6 w-6 text-red-600"
										/>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Delete Integration
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to delete {flowTypeToDelete?.name}
												? This action cannot be undone.
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={handleConfirmDelete}
									disabled={isDeleting}
								>
									{isDeleting ? "Deleting..." : "Delete"}
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => setConfirmModalOpen(false)}
									disabled={isDeleting}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ViewFlowType;
